import React, { useState, useEffect } from 'react';
import { MobileScannerPage } from './MobileScannerPage';
import { MobileGeneratePage } from './MobileGeneratePage';
import { MobileStatsPage } from './MobileStatsPage';
import { MobileProfilePage } from './MobileProfilePage';
import { LanguageProvider, useLanguage } from '../contexts/LanguageContext';

type MobileTab = 'scanner' | 'generate' | 'stats' | 'profile';

const MobileAppContent: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const [currentTab, setCurrentTab] = useState<MobileTab>('scanner');
  const [apiHealth, setApiHealth] = useState<'loading' | 'healthy' | 'error'>('loading');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  useEffect(() => {
    checkApiHealth();
    // Add mobile viewport meta tag if not present
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(meta);
    }
  }, []);

  const checkApiHealth = async () => {
    try {
      const response = await fetch('https://cf.qrsp.app/api/health');
      if (response.ok) {
        setApiHealth('healthy');
      } else {
        setApiHealth('error');
      }
    } catch (error) {
      setApiHealth('error');
    }
  };

  const tabs = [
    { id: 'scanner', name: t('nav.scanner'), icon: '📷', description: t('nav.scanner.desc') },
    { id: 'generate', name: t('nav.generate'), icon: '📱', description: t('nav.generate.desc') },
    { id: 'stats', name: t('nav.stats'), icon: '💰', description: t('nav.stats.desc') },
    { id: 'profile', name: t('nav.profile'), icon: '👤', description: t('nav.profile.desc') },
  ] as const;

  const renderContent = () => {
    switch (currentTab) {
      case 'scanner':
        return <MobileScannerPage />;
      case 'generate':
        return <MobileGeneratePage />;
      case 'stats':
        return <MobileStatsPage />;
      case 'profile':
        return <MobileProfilePage />;
      default:
        return <MobileScannerPage />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Status Bar */}
      <div className="bg-green-600 text-white p-2 text-sm font-medium">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${apiHealth === 'healthy' ? 'bg-green-300' : 'bg-red-300'}`}></div>
            <span>QRSP São Paulo</span>
            {apiHealth === 'healthy' && <span className="text-xs opacity-75">• {t('status.online')}</span>}
          </div>
          
          <button
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded"
          >
            {language === 'en' ? 'EN' : 'PT'}
          </button>
        </div>
      </div>

      {/* Language Menu */}
      {showLanguageMenu && (
        <div className="absolute top-12 right-2 bg-white rounded-lg shadow-lg z-50 overflow-hidden">
          <button
            onClick={() => {
              setLanguage('en');
              setShowLanguageMenu(false);
            }}
            className={`block w-full px-4 py-3 text-left text-sm hover:bg-gray-50 ${language === 'en' ? 'bg-green-50 text-green-600' : 'text-gray-900'}`}
          >
            🇺🇸 {t('language.english')}
          </button>
          <button
            onClick={() => {
              setLanguage('pt-BR');
              setShowLanguageMenu(false);
            }}
            className={`block w-full px-4 py-3 text-left text-sm hover:bg-gray-50 ${language === 'pt-BR' ? 'bg-green-50 text-green-600' : 'text-gray-900'}`}
          >
            🇧🇷 {t('language.portuguese')}
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {renderContent()}
      </div>

      {/* Bottom Navigation - Uber Style */}
      <div className="bg-white border-t border-gray-200 safe-area-bottom">
        <div className="grid grid-cols-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setCurrentTab(tab.id as MobileTab)}
              className={`p-3 flex flex-col items-center space-y-1 transition-colors ${
                currentTab === tab.id
                  ? 'text-green-600 bg-green-50'
                  : 'text-gray-500 active:bg-gray-100'
              }`}
            >
              <div className="text-2xl">{tab.icon}</div>
              <div className="text-xs font-medium">{tab.name}</div>
              {currentTab === tab.id && (
                <div className="w-12 h-0.5 bg-green-600 rounded-full"></div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const MobileApp: React.FC = () => {
  return (
    <LanguageProvider>
      <MobileAppContent />
    </LanguageProvider>
  );
};